import React, { useCallback, useEffect, useState, useRef, useMemo } from "react";
import { Close, ArrowUpward, ArrowDownward, Search, AddCircleOutlineRounded, CheckCircle, ZoomIn, ZoomOut, Search as SearchIcon, Fullscreen, FullscreenExit } from "@mui/icons-material";
import { Group as PanelGroup, Panel, Separator as PanelResizeHandle } from "react-resizable-panels";
//@ts-ignore
import BpmnViewer from "bpmn-js/lib/NavigatedViewer";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  List,
  ListItemButton,
  Paper,
  Stack,
  TextField,
  Typography,
  InputAdornment,
  Step,
  StepLabel,
  Stepper,
  Chip,
  Tooltip,
} from "@mui/material";
import {
  baseApi,
  flownodesApi,
  processesApi,
  organizationsApi,
  tagsApi,
} from "api";
import dayjs from "dayjs";
import { getTranslation } from "common";
import { getDefaultValueForType, getUserSchema, toBase64 } from "common/utilities";
import { PrioritiesDropDown } from "./PrioritiesDropDown";
import {
  useBreakpoint,
  useForm,
  useSelectedOrganization,
  useSnackbar,
  useTranslations,
} from "hooks";
import { isRequired, Validation } from "hooks/useForm";
import { FieldsList } from "components";
import { Priority } from "hooks/usePriorities";
import DropZone from "../DropZone";
import noModel from "../../image/empty-pana.svg";
import { useNavigate } from "react-router-dom";
import LabelManager from "../labels/LabelManager";
import { LabelData } from "components/labels/label_Interface";
import Labels from "components/labels/Labels";
import { SortState } from "hooks/useSortableList";
import { UserData } from "hooks/useUserDataKeys";
import FlowentiButton from "components/flowenti-ui-kit/FlowentiButton";
import { RichTextEditor } from "components/Editor/plugins/RichTextEditor";
import { processmodelsApi } from "api";

const INITIAL_STATE = {
  name: "",
  description_detail: "",
  meta: {},
  selectedProcess: null,
};

interface Process {
  assignmentmode: number;
  id: number;
  idorganization: number;
  idprocessmodel: number;
  idswimlane: number;
  idtype: number;
  name: string;
  processmodelname: string;
  swimlanename: string;
  userschema: string;
  descriptiondetail?: string | null;
}

interface StartProcessProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isAdmin?: boolean;
}

const StartProcess: React.FC<StartProcessProps> = ({
  open,
  setOpen,
  isAdmin = false,
}) => {
  const selectedOrganization = useSelectedOrganization();
  const [processes, setProcesses] = useState<Process[]>([]);
  const [selectedProcess, setSelectedProcess] = useState<Process | null>(null);
  const [allPriorities, setAllPriorities] = useState<Priority[]>([]);
  const [checked, setChecked] = useState<LabelData[]>([]);
  const [selectedPriority, setSelectedPriority] = useState<Priority | null>(null);
  const [openAddFieldDialog, setOpenAddFieldDialog] = useState(false);
  const administrator = true;

  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const handleFileChange = (files: File[] | null) => {
    if (files) setSelectedFiles(files);
  };

  const [isLabelManagerShown, setIsLabelManagerShown] = useState<HTMLElement | null>(null);
  const descriptionDetailRef = useRef<string>("");
  const handleLabelManagerClose = () => setIsLabelManagerShown(null);

  const { sendSnack } = useSnackbar();
  const translations = useTranslations();
  const validations: Validation[] = [
    ({ name }) =>
      isRequired(name) || { name: getTranslation(translations, "register.fieldrequired") },
  ];

  const { changeHandler, reset, values, errors, touched, isValid } = useForm(INITIAL_STATE, validations);
  const navigate = useNavigate();
  const isMobile = useBreakpoint("down", "md");

  const listRef = useRef<HTMLUListElement>(null);
  const itemRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});

  // Wizard step state
  const [step, setStep] = useState<number>(0);
  const [isDescriptionFocused, setIsDescriptionFocused] = useState<boolean>(false);
  const [zoomLevel, setZoomLevel] = useState<number>(1);
  const [isFullScreen, setIsFullScreen] = useState<boolean>(false);

  // Persist panel layouts in localStorage
  const STEP1_LAYOUT_KEY = "startprocess-step1-layout";
  const STEP2_LAYOUT_KEY = "startprocess-step2-layout";
  const loadLayout = (key: string): { [id: string]: number } | undefined => {
    try { const s = localStorage.getItem(key); return s ? JSON.parse(s) : undefined; } catch { return undefined; }
  };
  const saveLayout = (key: string, layout: { [id: string]: number }) => {
    try { localStorage.setItem(key, JSON.stringify(layout)); } catch {}
  };

  const scrollToSelectedProcess = useCallback(() => {
    if (selectedProcess && itemRefs.current[selectedProcess.id]) {
      itemRefs.current[selectedProcess.id]?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [selectedProcess]);

  useEffect(() => {
    if (open && selectedProcess?.userschema) {
      const schema = getUserSchema(selectedProcess.userschema);
      const newMeta: { [key: string]: UserData } = {};
      for (const item of schema) {
        newMeta[item.name] = {
          inCard: item.inCard,
          type: item.type,
          value: String(getDefaultValueForType(item.type)),
        };
      }
      changeHandler("meta", newMeta);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProcess?.userschema, open]);

  const [loading, setLoading] = useState<boolean>(false);
  const [sortOrder, setSortOrder] = useState<SortState>(SortState.Asc);
  const [searchQuery, setSearchQuery] = useState<string>("");

  const sortProcessesAsc = () => {
    setProcesses([...processes].sort((a, b) => a.processmodelname.localeCompare(b.processmodelname)));
    setSortOrder(SortState.Asc);
  };
  const sortProcessesDesc = () => {
    setProcesses([...processes].sort((a, b) => b.processmodelname.localeCompare(a.processmodelname)));
    setSortOrder(SortState.Desc);
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await baseApi.get(`startevents/organizations/${selectedOrganization?.id}`);
        const sortedProcesses = res.data.possiblestart.sort(
          (a: { processmodelname: string }, b: { processmodelname: any }) =>
            a.processmodelname.localeCompare(b.processmodelname)
        );
        setProcesses(sortedProcesses);
      } catch { }
    })();
  }, [selectedOrganization]);

  useEffect(() => {
    if (!open) {
      setChecked([]);
      reset();
      setSelectedFiles([]);
      descriptionDetailRef.current = "";
      setSearchQuery("");
      setStep(0);
      setIsDescriptionFocused(false);
      setIsFullScreen(false);
    }
  }, [processes, open, reset]);

  const filteredProcesses = useMemo(() => {
    if (!searchQuery.trim()) return processes;
    const query = searchQuery.toLowerCase();
    return processes.filter((process) => {
      const processName = process.name === ""
        ? process.processmodelname
        : `${process.processmodelname} (${process.name})`;
      return processName.toLowerCase().includes(query);
    });
  }, [processes, searchQuery]);

  const fetchOrganizationPriorities = useCallback(async () => {
    if (!selectedOrganization?.id) return;
    const response = await organizationsApi.get(`/${selectedOrganization?.id}/priorities`);
    if (response?.data?.priorities) {
      setAllPriorities(response.data.priorities);
    }
  }, [selectedOrganization]);

  useEffect(() => { fetchOrganizationPriorities(); }, [fetchOrganizationPriorities]);

  useEffect(() => {
    if (selectedOrganization?.id && allPriorities.length > 0 && !selectedPriority) {
      const defaultPriority = allPriorities[0];
      if (defaultPriority) setSelectedPriority(defaultPriority);
    }
  }, [selectedOrganization, allPriorities, selectedPriority]);

  const LAST_SELECTED_PROCESS_KEY = "lastSelectedProcess";
  const saveLastSelectedProcess = (process: any) => {
    try {
      localStorage.setItem(LAST_SELECTED_PROCESS_KEY, JSON.stringify({ id: process.id, name: process.name, processmodelname: process.processmodelname, timestamp: Date.now() }));
    } catch { }
  };
  const getLastSelectedProcess = () => {
    try {
      const saved = localStorage.getItem(LAST_SELECTED_PROCESS_KEY);
      return saved ? JSON.parse(saved) : null;
    } catch { return null; }
  };

  useEffect(() => {
    if (selectedProcess) saveLastSelectedProcess(selectedProcess);
  }, [selectedProcess]);

  useEffect(() => {
    if (open && processes.length > 0 && selectedProcess && itemRefs.current[selectedProcess.id]) {
      requestAnimationFrame(() => requestAnimationFrame(() => scrollToSelectedProcess()));
    }
  }, [open, processes.length, selectedProcess?.id, scrollToSelectedProcess]);

  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => scrollToSelectedProcess(), 300);
      return () => clearTimeout(timer);
    }
  }, [open, scrollToSelectedProcess]);

  useEffect(() => {
    if (processes.length === 0) { setSelectedProcess(null); return; }
    if (processes.length === 1) {
      const singleProcess = processes[0];
      if (!selectedProcess || selectedProcess.id !== singleProcess.id) setSelectedProcess(singleProcess);
      return;
    }
    if (!selectedProcess) {
      const lastSelected = getLastSelectedProcess();
      if (lastSelected) {
        const matchingProcess = processes.find((p) => p.id === lastSelected.id);
        if (matchingProcess) setSelectedProcess(matchingProcess);
      }
    }
  }, [processes, selectedProcess]);

  // BPMN Preview
  const bpmnContainerRef = useRef<HTMLDivElement>(null);
  const bpmnViewerRef = useRef<any>(null);

  const openDiagram = useCallback(async (xml: string) => {
    const container = bpmnContainerRef.current;
    if (!container) return;
    if (bpmnViewerRef.current) {
      try { bpmnViewerRef.current.destroy(); } catch { }
    }
    container.innerHTML = "";
    const viewer = new BpmnViewer({ container, readOnly: true, additionalModules: [] });
    bpmnViewerRef.current = viewer;
    try {
      await viewer.importXML(xml);
      (viewer.get("canvas") as any).zoom("fit-viewport");
      setZoomLevel(1);
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    if (step === 0 && selectedProcess) {
      // rAF ensures the Dialog has finished rendering and the ref is attached
      const raf = requestAnimationFrame(() => {
        (async () => {
          try {
            const { data } = await processmodelsApi.get(`/${selectedProcess.idprocessmodel}`);
            if (data.model) openDiagram(data.model);
          } catch { }
        })();
      });
      return () => cancelAnimationFrame(raf);
    }
    if (step === 0 && !selectedProcess && bpmnContainerRef.current) {
      bpmnContainerRef.current.innerHTML = "";
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProcess, step, openDiagram, open, isMobile]);

  // Cleanup viewer on unmount
  useEffect(() => {
    return () => {
      if (bpmnViewerRef.current) {
        try { bpmnViewerRef.current.destroy(); } catch { }
      }
    };
  }, []);

  const handleStartProcess = async ({ name, files, meta }: { name: string; meta: { [key: string]: UserData }; files: File[] }) => {
    setLoading(true);
    try {
      const payload = {
        idprocessmodel: selectedProcess?.idprocessmodel,
        idorganization: selectedOrganization?.id,
        idevent: selectedProcess?.id,
        startdate: dayjs().utc().toISOString(),
        enddate: null,
        description: name,
        descriptiondetail: descriptionDetailRef.current || null,
        userdata: meta,
      };
      const { data } = await processesApi.post("", payload);
      const processId = data.process.id;
      const labelPromises = checked.map((label) =>
        tagsApi.post(`/${selectedOrganization?.id}/processes/${processId}/tags/${label.id}`)
      );
      await Promise.all(labelPromises);
      const { previousprocesstoken: { id: processTokenId } } = data;
      const processtokenObject = data.processtoken;
      if (selectedPriority?.id && processtokenObject) {
        await flownodesApi.post(`/${processtokenObject.idprocess}/priorities/${selectedPriority.id}`, {});
      }
      if (files) {
        let hasError = false;
        for (let file of files) {
          const attachPayload = {
            idorganization: selectedOrganization?.id,
            filename: file.name,
            cloudlink: "",
            idtype: 1,
            description: file.name,
            blob: await toBase64(file),
          };
          try {
            await flownodesApi.post(`${processTokenId}/attachments`, attachPayload);
          } catch { hasError = true; }
        }
        if (!hasError && files.length > 0) {
          sendSnack({ type: "success", message: getTranslation(translations, "generic.fileuploadedsuccessfully") });
        }
      }
    } finally {
      reset();
      setSelectedProcess(null);
      setSelectedPriority(null);
      setOpen(false);
      setLoading(false);
    }
  };

  const processLabel = (p: Process) =>
    p.name === "" ? p.processmodelname : `${p.processmodelname} (${p.name})`;

  const renderStep1List = () => (
    <>
      <Box sx={{ p: 1.5, borderBottom: 1, borderColor: "divider" }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            placeholder={getTranslation(translations, "pm.search.model")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: "text.secondary" }} />
                </InputAdornment>
              ),
              sx: { borderRadius: 5 },
            }}
          />
          {filteredProcesses.length > 0 && (
            <IconButton
              onClick={sortOrder === SortState.Asc ? sortProcessesDesc : sortProcessesAsc}
              size="small"
              sx={{ borderRadius: 2 }}
            >
              {sortOrder === SortState.Asc ? <ArrowDownward /> : <ArrowUpward />}
            </IconButton>
          )}
        </Stack>
      </Box>
      <List
        ref={listRef}
        id="process-list"
        sx={{
          flex: 1,
          overflow: "auto",
          py: 0.5,
          px: 1,
          "&::-webkit-scrollbar": { width: "6px" },
          "&::-webkit-scrollbar-thumb": { backgroundColor: "rgba(0,0,0,0.2)", borderRadius: "4px" },
        }}
      >
        {filteredProcesses.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: "center" }}>
            {searchQuery
              ? getTranslation(translations, "pm.no.processes.found") || "Nessun processo trovato"
              : getTranslation(translations, "pm.no.processes.available") || "Nessun processo disponibile"}
          </Typography>
        ) : (
          filteredProcesses.map((process) => (
            <ListItemButton
              ref={(el) => (itemRefs.current[process.id] = el)}
              key={process.id}
              id={`startprocess-fields-${process.id}`}
              selected={selectedProcess?.id === process.id}
              sx={{
                borderRadius: 2,
                mb: 0.5,
                py: 1,
                "&.Mui-selected": {
                  backgroundColor: "primary.light",
                  "&:hover": { backgroundColor: "primary.light" },
                },
              }}
              onClick={() => {
                if (selectedProcess?.id !== process.id) {
                  setSelectedProcess(process);
                  saveLastSelectedProcess(process);
                }
              }}
            >
              <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
                <Typography variant="body2" noWrap sx={{ flex: 1, mr: 1 }}>
                  {processLabel(process)}
                </Typography>
                {selectedProcess?.id === process.id && (
                  <CheckCircle sx={{ color: "primary.main", fontSize: 16, flexShrink: 0 }} />
                )}
              </Stack>
            </ListItemButton>
          ))
        )}
      </List>
    </>
  );

  const renderStep1Preview = () => (
    <>
      {selectedProcess ? (
        <>
          <Box
            ref={bpmnContainerRef}
            sx={{
              flex: 1,
              width: "100%",
              height: "100%",
              pointerEvents: "none",
              "& .djs-palette": { display: "none" },
              "& .djs-palette-open": { float: "unset" },
            }}
          />
          {/* Name badge */}
          <Box sx={{
            position: "absolute",
            top: isMobile ? 12 : "unset",
            bottom: isMobile ? "unset" : 12,
            left: 12,
            bgcolor: "background.paper",
            border: 1,
            borderColor: "divider",
            borderRadius: 2,
            px: 1,
            py: 0.2,
            zIndex: 10
          }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem", lineHeight: 1 }}>
              {getTranslation(translations, "pm.startprocess.preview") || "Anteprima:"}{" "}
              <strong>{processLabel(selectedProcess)}</strong>
            </Typography>
          </Box>
          {/* Zoom controls */}
          <Box sx={{ position: "absolute", bottom: 12, right: 12, display: "flex", alignItems: "center", gap: 0.5, bgcolor: "background.paper", border: 1, borderColor: "divider", borderRadius: 5, px: 1, py: 0.5, zIndex: 10 }}>
            <IconButton
              size="small"
              sx={{ borderRadius: 2, p: 0.5 }}
              onClick={() => {
                const newZoom = Math.max(0.2, parseFloat((zoomLevel - 0.15).toFixed(2)));
                bpmnViewerRef.current?.get("canvas").zoom(newZoom);
                setZoomLevel(newZoom);
              }}
            >
              <ZoomOut fontSize="small" />
            </IconButton>
            <Tooltip title={getTranslation(translations, "pm.startprocess.fit_viewport") || "Adatta allo schermo"}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, cursor: "pointer", px: 0.5 }}
                onClick={() => {
                  bpmnViewerRef.current?.get("canvas").zoom("fit-viewport");
                  setZoomLevel(1);
                }}
              >
                <SearchIcon sx={{ fontSize: 14, color: "text.secondary" }} />
                <Typography variant="caption" sx={{ minWidth: 32, textAlign: "center", fontWeight: 600 }}>
                  {Math.round(zoomLevel * 100)}%
                </Typography>
              </Box>
            </Tooltip>
            <IconButton
              size="small"
              sx={{ borderRadius: 2, p: 0.5 }}
              onClick={() => {
                const newZoom = Math.min(3.0, parseFloat((zoomLevel + 0.15).toFixed(2)));
                bpmnViewerRef.current?.get("canvas").zoom(newZoom);
                setZoomLevel(newZoom);
              }}
            >
              <ZoomIn fontSize="small" />
            </IconButton>
            <Box sx={{ height: 16, width: "1px", bgcolor: "divider", mx: 0.5 }} />
            <Tooltip title={isFullScreen ? "Esci da schermo intero" : "Schermo intero"}>
              <IconButton
                size="small"
                sx={{ borderRadius: 2, p: 0.5 }}
                onClick={() => setIsFullScreen(!isFullScreen)}
              >
                {isFullScreen ? <FullscreenExit fontSize="small" /> : <Fullscreen fontSize="small" />}
              </IconButton>
            </Tooltip>
          </Box>
        </>
      ) : (
        <Stack flex={1} alignItems="center" justifyContent="center" spacing={1} sx={{ height: "100%" }}>
          <Typography variant="body2" color="text.secondary">
            {getTranslation(translations, "pm.startprocess.select_model") || "Seleziona un modello per visualizzare l'anteprima"}
          </Typography>
        </Stack>
      )}
    </>
  );

  const renderStep2Left = () => (
    <Stack spacing={3}>
      {/* Process name field with bold label */}
      <Box>
        <Typography variant="h6" mb={2}>
          {getTranslation(translations, "pm.processstart.processname.label") || "Nome processo"}
        </Typography>
        <TextField
          id="startprocess-name"
          fullWidth
          inputProps={{ maxLength: 7000 }}
          variant="outlined"
          size="small"
          error={Boolean(touched.name && errors.name)}
          helperText={touched.name && errors.name}
          value={values.name}
          onChange={({ target }) => changeHandler("name", target.value)}
          InputProps={{ sx: { borderRadius: 5 } }}
        />
      </Box>

      {/* RichTextEditor */}
      <Box>
        <Typography variant="h6" mb={2}>
          {getTranslation(translations, "pm.startprocess.description") || "Descrizione dettagliata"}
        </Typography>
        <RichTextEditor
          value={values.description_detail || ""}
          onChange={(markdown) => {
            descriptionDetailRef.current = markdown;
            changeHandler("description_detail", markdown);
          }}
          minHeight={isDescriptionFocused ? 180 : 120}
          onFocus={() => setIsDescriptionFocused(true)}
          onBlur={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget as Node)) {
              setIsDescriptionFocused(false);
            }
          }}
          sx={{
            width: "100%",
            "& [class*='_root_']": {
              fontSize: "0.9375rem",
            },
            "& [contenteditable]": {
              fontSize: "0.9375rem",
            },
            "& [contenteditable] h1": { fontSize: "1.4rem" },
            "& [contenteditable] h2": { fontSize: "1.2rem" },
            "& [contenteditable] h3": { fontSize: "1.05rem" },
            "& [contenteditable] table": { fontSize: "0.9375rem" },
            "& .cm-editor": { fontSize: "0.9375rem" },
          }}
        />
      </Box>

      <Box>
        <Typography variant="h6" mb={2}>
          {getTranslation(translations, "settings.attachments.label")}
        </Typography>
        <DropZone files={selectedFiles} onFileChange={handleFileChange} maxFiles={10} />
      </Box>
    </Stack>
  );

  const renderStep2Right = () => (
    <>
      {/* Labels card */}
      <Paper elevation={2} sx={{ p: 2, borderRadius: 2, boxShadow: "0 1px 6px rgba(0,0,0,0.10)", mb: 2, bgcolor: "grey.50", border: "1px solid rgba(0, 0, 0, 0.08)", maxWidth: isMobile ? "100%" : 280 }}>
        <Typography variant="subtitle2" fontWeight={700} mb={1.5}>
          {getTranslation(translations, "generic.labels.caption") || "Etichette"}
        </Typography>
        <FlowentiButton
          size="small"
          variant="outlined"
          component="label"
          onClick={(e: React.MouseEvent<HTMLElement>) => setIsLabelManagerShown(e.currentTarget)}
          fullWidth
        >
          {getTranslation(translations, "generic.labels.caption")}
        </FlowentiButton>
        {isLabelManagerShown && (
          <LabelManager
            checked={checked}
            setChecked={setChecked}
            open={isLabelManagerShown}
            handleClose={handleLabelManagerClose}
          />
        )}
      </Paper>

      {/* Priority card */}
      <Paper elevation={2} sx={{ p: 2, borderRadius: 2, boxShadow: "0 1px 6px rgba(0,0,0,0.10)", mb: 2, bgcolor: "grey.50", border: "1px solid rgba(0, 0, 0, 0.08)", maxWidth: isMobile ? "100%" : 280 }}>
        <Typography variant="subtitle2" fontWeight={700} mb={1.5}>
          {getTranslation(translations, "generic.priorities.caption") || "Priorità"}
        </Typography>
        <PrioritiesDropDown
          allPriorities={allPriorities}
          value={selectedPriority}
          onChange={(newPriority) => setSelectedPriority(newPriority)}
          administrator={administrator}
        />
      </Paper>

      {/* Fields card */}
      <Paper elevation={2} sx={{ p: 2, borderRadius: 2, boxShadow: "0 1px 6px rgba(0,0,0,0.10)", bgcolor: "grey.50", border: "1px solid rgba(0, 0, 0, 0.08)", maxWidth: isMobile ? "100%" : 280 }}>
        <Typography variant="subtitle2" fontWeight={700} mb={1.5}>
          {getTranslation(translations, "pm.mayactivities.addfield.label") || "Campi"}
        </Typography>
        <FlowentiButton
          id="addfield-button"
          startIcon={<AddCircleOutlineRounded />}
          size="small"
          variant="outlined"
          onClick={() => setOpenAddFieldDialog(true)}
          fullWidth
        >
          {getTranslation(translations, "pm.mayactivities.addfield.label")}
        </FlowentiButton>
        {selectedProcess?.userschema && (
          <Box mt={2}>
            <FieldsList
              linkChange
              enableUndefined
              handleSaveUserdata={async (newValues) => changeHandler("meta", newValues)}
              selectedProcess={selectedProcess?.id}
              schema={selectedProcess?.userschema}
              openAddFieldDialog={openAddFieldDialog}
              onAddFieldDialogClose={() => setOpenAddFieldDialog(false)}
              value={values.meta}
              onChange={(newValues) => changeHandler("meta", newValues)}
            />
          </Box>
        )}
      </Paper>
    </>
  );

  return (
    <Dialog
      open={Boolean(open)}
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile || isFullScreen}
      onClose={() => setOpen(false)}
      PaperProps={{
        sx: {
          borderRadius: (isMobile || isFullScreen) ? 0 : 2,
          height: (isMobile || isFullScreen) ? "100%" : "80vh",
          maxHeight: (isMobile || isFullScreen) ? "100%" : "80vh",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <DialogTitle sx={{ pb: 1, borderBottom: 1, borderColor: "divider" }}>
        <Stack direction={isMobile ? "column" : "row"} justifyContent="space-between" alignItems={isMobile ? "stretch" : "center"} spacing={isMobile ? 1.5 : 2}>
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="h6">
                {getTranslation(translations, "pm.startprocess.label.caption")}
              </Typography>
              <Labels labelCompleteMode labels={checked} />
            </Stack>
            {isMobile && (
              <IconButton onClick={() => setOpen(false)} sx={{ borderRadius: 2, p: 0.5 }}>
                <Close />
              </IconButton>
            )}
          </Stack>
          <Stack direction="row" alignItems="center" spacing={2} justifyContent={isMobile ? "center" : "flex-end"}>
            <Stepper
              activeStep={step}
              sx={{
                minWidth: isMobile ? "unset" : 340,
                width: isMobile ? "100%" : "auto",
                "& .MuiStepLabel-label": {
                  color: "#54bdc1 !important",
                  fontWeight: 600,
                  fontSize: isMobile ? "0.75rem" : "inherit",
                  "&.Mui-active": {
                    fontWeight: 700,
                  },
                  "&.Mui-completed": {
                    fontWeight: 700,
                  },
                },
                "& .MuiStepConnector-line": {
                  borderColor: "#54bdc1",
                  borderTopWidth: 2,
                },
                "& .MuiStepIcon-root": {
                  width: isMobile ? 18 : 27,
                  height: isMobile ? 18 : 27,
                  overflow: "visible",
                  "& circle": {
                    fill: "#fff",
                    stroke: "#e0e0e0",
                    strokeWidth: 1.5,
                  },
                  "& .MuiStepIcon-text": {
                    fill: "#e0e0e0",
                    fontWeight: "bold",
                    fontSize: isMobile ? "0.55rem" : "0.95rem",
                  },
                  "&.Mui-active": {
                    "& circle": {
                      fill: "#fff",
                      stroke: "#54bdc1",
                      strokeWidth: 2,
                    },
                    "& .MuiStepIcon-text": {
                      fill: "#54bdc1",
                      fontWeight: "bold",
                    },
                  },
                  "&.Mui-completed": {
                    "& circle": {
                      fill: "#fff",
                      stroke: "#54bdc1",
                      strokeWidth: 1.5,
                    },
                    "& path": {
                      fill: "#54bdc1",
                    },
                  },
                },
              }}
            >
              <Step>
                <StepLabel>{getTranslation(translations, "pm.startprocess.step1") || "Scelta Modello"}</StepLabel>
              </Step>
              <Step>
                <StepLabel>{getTranslation(translations, "pm.startprocess.step2") || "Dettagli"}</StepLabel>
              </Step>
            </Stepper>
            {!isMobile && (
              <IconButton onClick={() => setOpen(false)} sx={{ borderRadius: 2 }}>
                <Close />
              </IconButton>
            )}
          </Stack>
        </Stack>
      </DialogTitle>

      <DialogContent
        sx={{
          flex: 1,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          p: 0,
        }}
      >
        {processes.length === 0 ? (
          <Stack direction="column" alignItems="center" justifyContent="center" spacing={2} sx={{ flex: 1 }}>
            <Box component="img" alt="..." sx={{ width: isMobile ? "220px" : "308px", height: isMobile ? "220px" : "308px" }} src={noModel} />
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
              <Typography color="textSecondary" component="p" sx={{ mt: 1, fontSize: "1.2rem", fontWeight: "bold" }}>
                {getTranslation(translations, "generic.create_one_model.caption")}
              </Typography>
              {isAdmin && (
                <Button onClick={() => navigate("/dashboard/process/insert")} variant="outlined" size="large" sx={{ mt: 3, borderRadius: 5 }}>
                  {getTranslation(translations, "pm.label.button.process_name")}
                </Button>
              )}
            </Box>
          </Stack>
        ) : (
          <>
            {/* STEP 1: model selection + BPMN preview */}
            {step === 0 && (
              isMobile ? (
                <Box sx={{ display: "flex", flexDirection: "column", flex: 1, overflow: "hidden" }}>
                  {/* Left panel: process list */}
                  <Box sx={{ height: "45%", display: "flex", flexDirection: "column", overflow: "hidden" }}>
                    {renderStep1List()}
                  </Box>

                  {/* Divider */}
                  <Box sx={{ height: "1px", bgcolor: "divider" }} />

                  {/* Right panel: BPMN preview */}
                  <Box sx={{ height: "55%", position: "relative", overflow: "hidden", display: "flex", flexDirection: "column", background: "#f8f9fa" }}>
                    {renderStep1Preview()}
                  </Box>
                </Box>
              ) : (
                <PanelGroup orientation="horizontal" style={{ flex: 1, minHeight: 0 }} defaultLayout={loadLayout(STEP1_LAYOUT_KEY)} onLayoutChanged={(layout) => saveLayout(STEP1_LAYOUT_KEY, layout)}>
                  {/* Left panel: process list */}
                  <Panel id="step1-list" defaultSize="10%" minSize="8%" maxSize="50%" style={{ display: "flex", flexDirection: "column", overflow: "hidden" }}>
                    {renderStep1List()}
                  </Panel>

                  {/* Splitbar */}
                  <PanelResizeHandle style={{
                    width: "4px",
                    background: "rgba(0,0,0,0.08)",
                    cursor: "col-resize",
                    transition: "background 0.15s",
                  }} />

                  {/* Right panel: BPMN preview */}
                  <Panel id="step1-preview" defaultSize="70%" style={{ position: "relative", overflow: "hidden", display: "flex", flexDirection: "column", background: "#f8f9fa" }}>
                    {renderStep1Preview()}
                  </Panel>
                </PanelGroup>
              )
            )}

            {/* STEP 2: details form */}
            {step === 1 && (
              <Box sx={{ flex: 1, overflow: "auto", p: isMobile ? 2 : 3 }}>
                {/* Selected model recap */}
                <Stack direction={isMobile ? "column" : "row"} alignItems={isMobile ? "flex-start" : "center"} justifyContent="space-between" spacing={isMobile ? 1.5 : 2} sx={{ mb: 3, p: 2, bgcolor: "rgba(84, 189, 193, 0.08)", border: "1px solid rgba(84, 189, 193, 0.4)", borderRadius: 5 }}>
                  <Box>
                    <Typography variant="caption" color="primary" fontWeight={600} sx={{ textTransform: "uppercase", letterSpacing: 1 }}>
                      {getTranslation(translations, "pm.startprocess.selected_model") || "Modello selezionato"}
                    </Typography>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {selectedProcess ? processLabel(selectedProcess) : ""}
                    </Typography>
                  </Box>
                  <Typography
                    onClick={() => setStep(0)}
                    sx={{
                      color: "primary.dark",
                      fontSize: "0.72rem",
                      fontWeight: 600,
                      cursor: "pointer",
                      "&:hover": {
                        textDecoration: "underline",
                      },
                    }}
                  >
                    {getTranslation(translations, "pm.startprocess.change_model") || "Cambia modello"}
                  </Typography>
                </Stack>

                {isMobile ? (
                  <Stack spacing={3}>
                    <Box sx={{ p: 1, border: "1px solid", borderColor: "divider", borderRadius: 2, bgcolor: "background.paper" }}>
                      {renderStep2Left()}
                    </Box>
                    <Stack spacing={2}>
                      {renderStep2Right()}
                    </Stack>
                  </Stack>
                ) : (
                  <PanelGroup orientation="horizontal" style={{ flex: 1, overflow: "hidden", height: "100%" }} defaultLayout={loadLayout(STEP2_LAYOUT_KEY)} onLayoutChanged={(layout) => saveLayout(STEP2_LAYOUT_KEY, layout)}>
                    {/* Left panel 60%: name, description, attachments */}
                    <Panel id="step2-left" defaultSize="60%" minSize="35%" style={{ display: "flex", flexDirection: "column", overflow: "auto", padding: "24px 16px 24px 24px" }}>
                      {renderStep2Left()}
                    </Panel>

                    {/* Splitbar */}
                    <PanelResizeHandle style={{
                      width: "6px",
                      background: "rgba(0,0,0,0.06)",
                      cursor: "col-resize",
                      transition: "background 0.15s",
                    }} />

                    {/* Right panel 40%: labels, priorities, fields */}
                    <Panel id="step2-right" defaultSize="40%" minSize="25%" style={{ display: "flex", flexDirection: "column", overflow: "auto", padding: "24px 24px 24px 16px", gap: 16 }}>
                      {renderStep2Right()}
                    </Panel>
                  </PanelGroup>
                )}
              </Box>
            )}
          </>
        )}
      </DialogContent>

      {processes.length > 0 && (
        <DialogActions sx={{ px: isMobile ? 2 : 3, py: 2, borderTop: 1, borderColor: "divider" }}>
          {step === 0 ? (
            <Stack direction="row" justifyContent="space-between" width="100%" spacing={2}>
              <FlowentiButton
                id="cancel-button-process"
                variant="outlined"
                size="medium"
                onClick={() => setOpen(false)}
                sx={{ flex: isMobile ? 1 : "none" }}
              >
                {getTranslation(translations, "pm.startprocess.button.cancel.caption")}
              </FlowentiButton>
              <FlowentiButton
              id="next-button-process"
                variant="contained"
                color="primary"
                size="medium"
                disabled={!selectedProcess}
                onClick={() => setStep(1)}
                sx={{ borderRadius: 5, flex: isMobile ? 1 : "none" }}
              >
                {getTranslation(translations, "generic.next.caption") || "Continua"}
              </FlowentiButton>
            </Stack>
          ) : (
            isMobile ? (
              <Stack spacing={1.5} width="100%">
                <Stack direction="row" spacing={2} width="100%">
                  <FlowentiButton
                    variant="outlined"
                    size="medium"
                    onClick={() => {
                      setStep(0);
                      setTimeout(() => scrollToSelectedProcess(), 150);
                    }}
                    sx={{ flex: 1 }}
                  >
                    {getTranslation(translations, "generic.back.caption") || "Indietro"}
                  </FlowentiButton>
                  <FlowentiButton
                    id="startprocess-button2"
                    variant="contained"
                    color="primary"
                    size="medium"
                    disabled={!isValid || !selectedProcess || loading}
                    onClick={() => handleStartProcess({ ...values, files: selectedFiles })}
                    sx={{ borderRadius: 5, flex: 1 }}
                  >
                    {getTranslation(translations, "pm.startprocess.button.ok.caption")}
                  </FlowentiButton>
                </Stack>
                <FlowentiButton
                  id="cancel-button-process"
                  variant="text"
                  size="medium"
                  onClick={() => setOpen(false)}
                  fullWidth
                >
                  {getTranslation(translations, "pm.startprocess.button.cancel.caption")}
                </FlowentiButton>
              </Stack>
            ) : (
              <Stack direction="row" justifyContent="space-between" width="100%">
                <FlowentiButton
                  id="cancel-button-process"
                  variant="outlined"
                  size="medium"
                  onClick={() => setOpen(false)}
                >
                  {getTranslation(translations, "pm.startprocess.button.cancel.caption")}
                </FlowentiButton>
                <Stack direction="row" spacing={2}>
                  <FlowentiButton
                    variant="outlined"
                    size="medium"
                    onClick={() => {
                      setStep(0);
                      setTimeout(() => scrollToSelectedProcess(), 150);
                    }}
                  >
                    {getTranslation(translations, "generic.back.caption") || "Indietro"}
                  </FlowentiButton>
                  <FlowentiButton
                    id="startprocess-button2"
                    variant="contained"
                    color="primary"
                    size="medium"
                    disabled={!isValid || !selectedProcess || loading}
                    onClick={() => handleStartProcess({ ...values, files: selectedFiles })}
                    sx={{ borderRadius: 5 }}
                  >
                    {getTranslation(translations, "pm.startprocess.button.ok.caption")}
                  </FlowentiButton>
                </Stack>
              </Stack>
            )
          )}
        </DialogActions>
      )}
    </Dialog>
  );
};

export default StartProcess;
